import styled from 'styled-components';
import _pick from 'lodash/pick';
import React, {FC} from 'react';

import {
  fontSizesMixins,
  mediaBreakpointDown,
  mediaBreakpointUp,
  palette,
  fontWeights,
  mediaBreakpointOnly
} from '../../../../lib/styleVariables';
import {paletteR4Secondary} from '../../../../lib/styleVariablesR4';
import {PrimaryButton, SecondaryButton, TertiaryLink} from '../../../molecules/buttonsR4';
import {StylableProps} from '../../../../lib/styleHelpers';
import {ImageProps, Image} from '../../../common/image';

export interface ImageTextDescButtonCssProps {
  containerCss?: string;
  contentCss?: string;
  contentWrapperCss?: string;
  titleCss?: string;
  descriptionCss?: string;
  mediaWrapperCss?: string;
}

const descButtonPropsCssKeys: (keyof ImageTextDescButtonCssProps)[] = [
  'containerCss',
  'contentCss',
  'contentWrapperCss',
  'titleCss',
  'descriptionCss',
  'mediaWrapperCss'
];

export const getCssProps = (props: any) => _pick(props, descButtonPropsCssKeys);

export interface ImageTextDescButtonProps extends ImageTextDescButtonCssProps, StylableProps {
  category?: string;
  title: React.ReactNode;
  content: React.ReactNode;
  primaryButtonText?: string;
  primaryButtonLink?: string;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  tertiaryButtonText?: string;
  tertiaryButtonLink?: string;
  videoSrc?: string;
  imgSrc?: ImageProps['fluid'];
  mediaType: 'image' | 'video';
  mediaPosition: 'left' | 'right';
  isHighlighted: boolean;
  isImageClickable?: boolean;
}

const StyledContainer = styled.section<{isHighlighted: boolean; $css?: string}>`
  width: 100%;
  display: flex;
  align-items: center;
  background: ${props => (props.isHighlighted ? `${palette.white.base}` : '')};
  border-radius: ${props => (props.isHighlighted ? '10px' : '')};
  padding: ${props => (props.isHighlighted ? '16px' : '')};
  overflow: hidden;

  ${mediaBreakpointDown('tablet')} {
    padding-top: ${props => (props.isHighlighted ? '24px' : '')};
    flex-direction: column;
  }
  ${props => props.$css}
`;

interface MediaWrapperProps {
  $css?: string;
  isMediaOnLeft: boolean;
  isSmall: boolean;
  isVideo: boolean;
}

const StyledMediaWrapper = styled.div<MediaWrapperProps>`
  width: 50%;
  max-width: ${props => (props.isSmall ? '500px' : '600px')};
  order: ${props => (props.isMediaOnLeft ? '-1' : '1')};
  border-radius: ${props => (props.isVideo ? '0' : '10px')};
  overflow: hidden;

  ${mediaBreakpointDown('tablet')} {
    width: 100%;
    max-width: 600px;
    margin: 0 auto;
    order: 1;
  }
  ${props => props.$css}
`;

const StyledContentWrapper = styled.div<{$css?: string}>`
  align-self: center;
  display: flex;
  justify-content: center;

  ${mediaBreakpointDown('tablet')} {
    margin-bottom: 40px;
  }

  ${mediaBreakpointUp('desktop')} {
    flex: 1 0 0;
  }
  ${props => props.$css}
`;

const StyledContent = styled.div<{$css?: string}>`
  ${mediaBreakpointDown('tablet')} {
    align-items: center;
    display: flex;
    flex-direction: column;
    margin-left: auto;
    margin-right: auto;
    max-width: 600px;
    text-align: center;
  }

  ${mediaBreakpointUp('desktop')} {
    max-width: 438px;
    padding: 24px;
  }
  ${props => props.$css}
`;

const StyledEyebrow = styled.div`
  ${fontSizesMixins.text3()};
  margin-bottom: 16px;
  text-transform: capitalize;
`;

const StyledTitle = styled.h2<{$css?: string}>`
  ${fontSizesMixins.h2()}
  margin-top: 0;
  margin-bottom: 16px;
  word-break: break-word;
  ${mediaBreakpointDown('mobile')} {
    font-weight: ${fontWeights.normal};
  }
  ${props => props.$css}
`;

const StyledDescription = styled.div<{$css?: string}>`
  ${fontSizesMixins.text2()}
  margin:0;

  ${mediaBreakpointDown('tablet')} {
    ${fontSizesMixins.text3()}
  }
  ${props => props.$css}
`;

const buttonsWrapperStyle = `
  margin-top: 8px;
  flex-direction: column;
`;

const StyledButtonsWrapper = styled.div<{hasTertiaryButton: boolean}>`
  margin-top: 24px;
  display: flex;
  flex-wrap: wrap;

  ${mediaBreakpointDown('mobile')} {
    flex-direction: column;
    align-items: center;
  }

  ${mediaBreakpointOnly('tablet')} {
    justify-content: center;
  }
  ${props => props.hasTertiaryButton && buttonsWrapperStyle}
`;

const buttonStyle = `
  ${mediaBreakpointUp('tablet')} {
    &:not(:last-child) {
      margin-right: 16px;
    }
  }

  ${mediaBreakpointDown('mobile')} {
    &:not(:last-child) {
      margin-bottom: 12px;
    }
  }
`;

const buttonsStyle = `
  ${mediaBreakpointUp('tablet')} {
    &:not(:last-child) {
      margin-right: 0;
      margin-bottom: 12px;
    }
  }
`;

const StyledPrimaryButton = styled(PrimaryButton)<{hasTertiaryButton: boolean}>`
  ${buttonStyle}
  ${props => props.hasTertiaryButton && buttonsStyle}
`;
const StyledSecondaryButton = styled(SecondaryButton)<{hasTertiaryButton: boolean}>`
  ${buttonStyle}
  ${props => props.hasTertiaryButton && buttonsStyle}
`;
const StyledTertiaryButton = styled(TertiaryLink)`
  ${buttonStyle}
  color: ${paletteR4Secondary.blue.lowlight};
`;

const mediaStyle = `
  width: 100%;
  vertical-align: middle;
  ${mediaBreakpointDown('tablet')} {
    object-position: center left;
  }
`;

const StyledClickableImage = styled.a`
  display: block;
`;

const StyledImage = styled(Image)<{isMediaOnLeft: boolean}>`
  object-fit: contain;

  ${mediaBreakpointUp('desktop')} {
    object-position: center ${props => (props.isMediaOnLeft ? 'left' : 'right')};
  }

  > img,
  > picture img {
    object-fit: contain !important;
  }

  ${mediaStyle}
`;

const StyledVideo = styled.video<{isMediaOnLeft: boolean}>`
  align-self: center;
  object-fit: contain;

  ${mediaBreakpointUp('desktop')} {
    object-position: center ${props => (props.isMediaOnLeft ? 'left' : 'right')};
  }

  ${mediaStyle}
`;

export const ImageTextDescButton: FC<ImageTextDescButtonProps> = props => {
  const {
    category,
    title,
    content,
    primaryButtonText,
    primaryButtonLink,
    secondaryButtonText,
    secondaryButtonLink,
    tertiaryButtonText,
    tertiaryButtonLink,
    videoSrc,
    imgSrc,
    mediaType,
    mediaPosition,
    isHighlighted,
    containerCss,
    contentCss,
    contentWrapperCss,
    titleCss,
    descriptionCss,
    mediaWrapperCss,
    isImageClickable
  } = props;
  const isMediaOnLeft = mediaPosition === 'left';
  const hasPrimaryButton = primaryButtonText && primaryButtonLink;
  const hasSecondaryButton = secondaryButtonLink && secondaryButtonText;
  const hasTertiaryButton = Boolean(tertiaryButtonLink && tertiaryButtonText);
  const hasAtLeastOneButton = hasPrimaryButton || hasSecondaryButton || hasTertiaryButton;
  const hasVideo = mediaType === 'video';
  const hasImage = mediaType === 'image';

  return (
    <StyledContainer isHighlighted={isHighlighted} className={props.className} $css={containerCss}>
      <StyledContentWrapper $css={contentWrapperCss}>
        <StyledContent $css={contentCss}>
          {category && <StyledEyebrow>{category}</StyledEyebrow>}
          <StyledTitle $css={titleCss}>{title}</StyledTitle>
          <StyledDescription $css={descriptionCss}>{content}</StyledDescription>
          {hasAtLeastOneButton && (
            <StyledButtonsWrapper hasTertiaryButton={hasTertiaryButton}>
              {hasPrimaryButton && (
                <StyledPrimaryButton href={primaryButtonLink} hasTertiaryButton={hasTertiaryButton}>
                  {primaryButtonText}
                </StyledPrimaryButton>
              )}

              {hasSecondaryButton && (
                <StyledSecondaryButton href={secondaryButtonLink} hasTertiaryButton={hasTertiaryButton}>
                  {secondaryButtonText}
                </StyledSecondaryButton>
              )}

              {hasTertiaryButton && (
                <StyledTertiaryButton href={tertiaryButtonLink}>{tertiaryButtonText}</StyledTertiaryButton>
              )}
            </StyledButtonsWrapper>
          )}
        </StyledContent>
      </StyledContentWrapper>

      <StyledMediaWrapper
        $css={mediaWrapperCss}
        isVideo={hasVideo}
        isMediaOnLeft={isMediaOnLeft}
        isSmall={isHighlighted}
      >
        {hasImage &&
          (isImageClickable
            ? hasImage && (
              <StyledClickableImage href={primaryButtonLink || secondaryButtonLink || tertiaryButtonLink}>
                <StyledImage isMediaOnLeft={isMediaOnLeft} fluid={imgSrc} />
              </StyledClickableImage>
            )
            : hasImage && <StyledImage isMediaOnLeft={isMediaOnLeft} fluid={imgSrc} />)}
        {hasVideo && (
          <StyledVideo isMediaOnLeft={isMediaOnLeft} autoPlay loop muted playsInline>
            <source src={`${videoSrc}#t=0.1`} />
          </StyledVideo>
        )}
      </StyledMediaWrapper>
    </StyledContainer>
  );
};
